import * as React from 'react';
import { Alert, Flex, Input, InputGroup, InputLeftElement } from '@chakra-ui/react';

import { UndoEdit } from '../../iconButtons/UndoEdit';
import { withConfirm } from '../withConfirm';
import { CustomFormControl, DirectionProp } from '../CustomFormControl';

interface IProps extends DirectionProp {
  prefixText: string;
  prefixColor?: string;
  id?: string;
  name?: string;
  value?: string | number;
  label?: string;
  subText?: string;
  type?: string;
  autoFocus?: boolean;
  inputHelperText?: string;
  warningText?: string;
  errorText?: string;
  forwardedRef?: ((instance: HTMLInputElement | null) => void) | React.RefObject<HTMLInputElement> | null | undefined;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  canUndo?: boolean;
  onUndo?: () => void;
  undoSize?: 'lg' | 'md' | 'sm';
  size?: 'lg' | 'md' | 'sm';
  mb?: number | string;
  disabled?: boolean;
  currencySymbol?: string;
}

interface IState {
  focused: boolean;
}

class PrefixInput extends React.Component<IProps, IState> {
  constructor(props: IProps & IState) {
    super(props);
    this.state = {
      focused: false,
    };
  }

  onBlur = (evt: React.FocusEvent<HTMLInputElement>) => {
    this.setState({
      focused: false,
    });
    if (this.props.onBlur) this.props.onBlur(evt);
  };

  onFocus = (evt: React.FocusEvent<HTMLInputElement>) => {
    this.setState({
      focused: true,
    });
    if (this.props.onFocus) this.props.onFocus(evt);
  };

  onChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    // disallow any non numeric keys
    const { value } = evt.currentTarget;

    if (!/^\d*$/.test(value)) {
      return false;
    }
    return this.props.onChange?.(evt);
  };

  render() {
    const {
      id,
      name,
      label,
      value,
      type,
      subText,
      prefixText,
      prefixColor,
      errorText,
      warningText,
      forwardedRef,
      disabled,
      canUndo,
      onUndo,
      undoSize,
      autoFocus,
      size = 'lg',
      mb = 0,
      direction,
    } = this.props;

    return (
      <CustomFormControl
        id={id}
        controlProps={{
          isInvalid: !!errorText,
          mb,
        }}
        direction={direction}
        size={size}
        label={label}
        subLabel={subText}
        error={errorText}
      >
        <Flex gap={{ base: 1, lg: 2 }}>
          <InputGroup data-selector="input-wrapper" size={size}>
            <InputLeftElement pointerEvents="none" color={prefixColor || 'utility-icon'}>
              {prefixText}
            </InputLeftElement>

            <Input
              name={name}
              data-selector={id || 'input-field'}
              onBlur={this.onBlur}
              onChange={this.onChange}
              onFocus={this.onFocus}
              type={type}
              inputMode="numeric"
              value={value}
              autoFocus={autoFocus}
              ref={forwardedRef}
              isInvalid={!!errorText}
              onPaste={(evt: React.ClipboardEvent<HTMLInputElement>) => {
                evt.preventDefault();
                return false;
              }}
              autoComplete="off"
              maxLength={10}
              isDisabled={disabled}
            />
          </InputGroup>

          {canUndo && <UndoEdit id="undo" onUndo={onUndo} size={undoSize} />}
        </Flex>

        {warningText ? (
          <Alert status="warning" data-selector="warning-text" mt={3}>
            {warningText}
          </Alert>
        ) : null}
      </CustomFormControl>
    );
  }
}

const PrefixInputWithConfirm = withConfirm((props: IProps) => {
  const { currencySymbol, ...rest } = props;
  return <PrefixInput {...rest} prefixText={currencySymbol ? currencySymbol : '£'} prefixColor="chakra-body-text" />;
});

export { PrefixInput, PrefixInputWithConfirm };
